import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  Skeleton,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  Person,
  Share,
  ArrowForward,
  AccessTime,
  School,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { getBlogBySlug, getPublicBlogs, getImageUrl } from '../services/api';
import { scrollToSection } from '../utils/scroll';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await getBlogBySlug(slug);
        const blogData = response.data.data;
        setBlog(blogData);

        // Fetch related blogs from same category (excluding current blog)
        const relatedResponse = await getPublicBlogs({
          category: blogData.category,
          limit: 3,
        });
        const filtered = (relatedResponse.data.data || []).filter(
          (b) => b._id !== blogData._id
        );
        setRelatedBlogs(filtered.slice(0, 3));
      } catch (err) {
        setError(err.message || 'Blog not found');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  // Loading State
  if (loading) {
    return (
      <Box sx={{ py: 8, minHeight: '80vh' }}>
        <Container maxWidth="md">
          <Skeleton variant="text" width={80} height={40} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={400} sx={{ mb: 4, borderRadius: 3 }} />
          <Skeleton variant="text" width="60%" height={50} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 4 }} />
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={20} />
        </Container>
      </Box>
    );
  }

  // Error State
  if (error || !blog) {
    return (
      <Box sx={{ py: 8, minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <School sx={{ fontSize: 80, color: 'grey.300', mb: 3 }} />
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            Blog Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {error || 'The blog you are looking for does not exist or has been removed.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
          >
            Back to Home
          </Button>
        </Container>
      </Box>
    );
  }

  const readingTime = Math.ceil((blog.content || '').split(' ').length / 200);

  return (
    <>
      <Helmet>
        <title>{blog.title} - Learn Rabbit</title>
        <meta name="description" content={blog.excerpt} />
      </Helmet>

      {/* ===== BLOG HEADER ===== */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 8 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -80,
            right: -80,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#FFFFFF', 0.03)} 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              color: 'rgba(255,255,255,0.8)',
              mb: 4,
              '&:hover': { color: 'white', bgcolor: alpha('#FFF', 0.08) },
            }}
          >
            Back to Home
          </Button>

          {/* Category & Date */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label={blog.category}
              color="secondary"
              size="medium"
              sx={{ fontWeight: 700, fontSize: '0.85rem' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {new Date(blog.createdAt).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {readingTime} min read
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
              lineHeight: 1.2,
              mb: 3,
            }}
          >
            {blog.title}
          </Typography>

          {/* Author */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 48, height: 48 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
                {blog.author}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Author
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ===== BLOG CONTENT ===== */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          {/* Featured Image */}
          {blog.featuredImage && (
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                mb: 5,
                boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                component="img"
                src={getImageUrl(blog.featuredImage)}
                alt={blog.title}
                sx={{
                  width: '100%',
                  maxHeight: 500,
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          )}

          {/* Excerpt */}
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              fontWeight: 500,
              color: 'text.secondary',
              lineHeight: 1.7,
              fontStyle: 'italic',
              borderLeft: `4px solid ${theme.palette.secondary.main}`,
              pl: 3,
              py: 1,
            }}
          >
            {blog.excerpt}
          </Typography>

          {/* Content */}
          <Typography
            variant="body1"
            component="div"
            sx={{
              lineHeight: 1.9,
              fontSize: '1.1rem',
              color: 'text.primary',
              '& p': { mb: 3 },
              '& h2': { fontSize: '1.8rem', fontWeight: 700, mt: 5, mb: 2 },
              '& h3': { fontSize: '1.4rem', fontWeight: 600, mt: 4, mb: 2 },
              '& ul, & ol': { mb: 3, pl: 4 },
              '& li': { mb: 1 },
              '& img': {
                maxWidth: '100%',
                borderRadius: 2,
                my: 3,
              },
              '& blockquote': {
                borderLeft: `4px solid ${theme.palette.secondary.main}`,
                pl: 3,
                py: 1,
                my: 3,
                fontStyle: 'italic',
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderRadius: 1,
              },
            }}
          >
            {/* Render content with proper whitespace handling */}
            {blog.content.split('\n').map((paragraph, idx) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return null;
              
              // Check if it's a heading
              if (trimmed.startsWith('## ')) {
                return <Typography key={idx} variant="h3">{trimmed.replace('## ', '')}</Typography>;
              }
              if (trimmed.startsWith('### ')) {
                return <Typography key={idx} variant="h4" sx={{ fontWeight: 600, mt: 4, mb: 2 }}>{trimmed.replace('### ', '')}</Typography>;
              }
              if (trimmed.startsWith('- ')) {
                return (
                  <Box key={idx} component="ul" sx={{ mb: 2 }}>
                    <li>{trimmed.replace('- ', '')}</li>
                  </Box>
                );
              }
              return (
                <Typography key={idx} paragraph sx={{ mb: 2 }}>
                  {trimmed}
                </Typography>
              );
            })}
          </Typography>

          {/* Share Section */}
          <Divider sx={{ my: 5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {blog.category && (
                <Chip
                  label={blog.category}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<Share />}
              size="small"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: blog.title,
                    text: blog.excerpt,
                    url: window.location.href,
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
            >
              Share
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== RELATED BLOGS ===== */}
      {relatedBlogs.length > 0 && (
        <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
          <Container maxWidth="xl">
            <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
              Related{' '}
              <Box component="span" sx={{ color: 'secondary.main' }}>
                Articles
              </Box>
            </Typography>
            <Grid container spacing={4}>
              {relatedBlogs.map((relatedBlog) => (
                <Grid item xs={12} sm={6} md={4} key={relatedBlog._id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                    onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(relatedBlog.featuredImage) || 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400'}
                      alt={relatedBlog.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 3 }}>
                      <Chip
                        label={relatedBlog.category}
                        size="small"
                        color="secondary"
                        sx={{ mb: 2, fontWeight: 600 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, minHeight: 56 }}>
                        {relatedBlog.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 2,
                        }}
                      >
                        {relatedBlog.excerpt}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForward />}
                        sx={{ fontWeight: 600 }}
                      >
                        Read More
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ===== BOTTOM CTA ===== */}
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          bgcolor: theme.palette.primary.main,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Start Your Medical Journey?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mb: 4 }}>
            Get free expert counselling and guidance for MBBS admissions in India and abroad.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => {
              navigate('/');
              setTimeout(() => scrollToSection('contact'), 100);
            }}
            sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
          >
            Get Free Counselling
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default BlogDetailPage;